<template>
    <div class="tw-bg-white tw-rounded-lg tw-shadow-smx tw-border tw-overflow-hidden tw-border-gray-200">
        <!-- Empty State -->
        <div v-if="!loading && !items.length" class="tw-p-8 tw-text-center">
            <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">
                <img src="/assets/images/empty-state/companies.svg" alt="No sponsors" class="tw-w-48 tw-h-48 tw-mb-4" />
                <h3 class="tw-text-lg tw-font-medium tw-text-gray-900">{{ $t('common.no-sponsors-found') }}</h3>
                <p class="tw-text-sm tw-text-gray-500">{{ $t('common.no-sponsors-description') }}</p>
            </div>
        </div>

        <div v-else class="tw-rounded tw-overflow-auto tw-border tw-border-solid tw-border-gray-100">
            <table class="tw-min-w-full tw-leading-normal">
                <thead>
                    <tr>
                        <th v-for="column in columns" :key="column.name" :class="column.classes"
                            class="tw-px-4 last:tw-border-e-0 tw-whitespace-nowrap tw-border-e tw-py-3 tw-border-b tw-borderx tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-text-[12px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
                            {{ $t(column.label) }}
                        </th>
                    </tr>
                </thead>

                <tbody v-if="loading">
                    <tr class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50"
                        v-for="i in 10" :key="i">
                        <td v-for="column in columns" :key="column.name" class="tw-px-2 tw-py-3">
                            <div class="tw-w-full tw-py-3 tw-bg-gray-50 group-even:tw-bg-white tw-rounded tw-min-w-[30px] tw-animate-pulse">
                            </div>
                        </td>
                    </tr>
                </tbody>

                <tbody v-else>
                    <SponsorTableRow
                        v-for="sponsor in items"
                        :key="sponsor.id"
                        :sponsor="sponsor"
                        @edit="$emit('edit', sponsor)"
                        @delete="handleDelete(sponsor)"
                        @toggle-status="handleToggleStatus"
                    />
                </tbody>
            </table>
        </div>

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            v-if="showDeleteModal"
            :visible="showDeleteModal"
            :title="$t('common.delete-sponsor')"
            :message="$t('common.delete-sponsor-confirmation')"
            @confirm="confirmDelete"
            @cancel="cancelDelete"
        />
    </div>

    <!-- Pagination -->
    <div class="tw-mt-2">
        <TablePagination 
            v-if="items.length"
            :options="options"
            @next="$emit('next')"
            @previous="$emit('previous')"
        />
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import SponsorTableRow from './SponsorTableRow.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import useAlert from '@/composables/useAlert';
import sponsorsApi from '@/api/sponsors';

const { t } = useI18n();

defineProps({
    loading: {
        type: Boolean,
        default: false
    },
    items: {
        type: Array,
        default: () => []
    },
    options: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['edit', 'delete', 'next', 'previous']);

// Column definitions
const columns = ref([
    { name: 'image', label: 'fields.image', classes: ['tw-w-[70px]'] },
    { name: 'name', label: 'fields.name', classes: ['tw-min-w-[200px]'] },
    { name: 'link', label: 'fields.link', classes: ['tw-min-w-[300px]'] },
    { name: 'status', label: 'fields.status', classes: ['tw-w-[120px]'] },
    { name: 'created_at', label: 'fields.created_at', classes: ['tw-w-[150px]'] },
    { name: 'actions', label: 'fields.actions', classes: ['tw-w-[120px]'] },
]);

// Delete Modal State
const showDeleteModal = ref(false);
const sponsorToDelete = ref(null);

const handleDelete = (sponsor) => {
    emit('delete', sponsor);
};

const cancelDelete = () => {
    sponsorToDelete.value = null;
    showDeleteModal.value = false;
};

const confirmDelete = async () => {
    try {
        const response = await sponsorsApi.admin.destroy(sponsorToDelete.value.id);
        if (response?.data) {
            useAlert(response.data.message);
            emit('delete');
        }
    } catch (error) {
        console.error('Error deleting sponsor:', error);
        useAlert(t('errors.something-went-wrong'), 'danger');
    } finally {
        cancelDelete();
    }
};

const handleToggleStatus = async (sponsor) => {
    try {
        const response = await sponsorsApi.admin.toggleStatus(sponsor.id);
        if (response?.data) {
            useAlert(response.data.message);
            emit('delete'); // Refresh the list
        }
    } catch (error) {
        console.error('Error toggling sponsor status:', error);
        useAlert(t('errors.something-went-wrong'), 'danger');
    }
};
</script>
